import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Grid2x2, Users, User, Crown, Briefcase, HandHeart, Sparkles, IdCard, Search, ChevronRight, Layers, Target, ArrowLeft, UserPlus, Calendar, CalendarPlus, Camera, Plus, Trash2, Edit3, X, Upload, MapPin, Clock, Flame, Baby, Wallet, TrendingUp, Printer, FileText, Ticket, Trophy, ArrowDownCircle, ArrowUpCircle, PiggyBank, QrCode, Check } from "lucide-react";
import { KasDialog, UndianDialog, UndianEventDetail } from "./bidang/WadahDialogs";

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });
}

// Wadah + Komisi metadata
const ORGS = [
  {
    code: "PERIA",
    title: "PERIA",
    subtitle: "Persekutuan Pria",
    description: "Wadah pelayanan kaum bapak GPDP untuk pertumbuhan iman, keluarga, dan pelayanan masyarakat.",
    icon: Users,
    gradient: "from-[#17365D] to-[#276AA3]",
    ring: "ring-[#276AA3]/30",
    accent: "#276AA3",
    filterAnggota: "PERIA",
  },
  {
    code: "PERNITA",
    title: "PERNITA",
    subtitle: "Persekutuan Wanita",
    description: "Wadah pelayanan kaum ibu, membangun keluarga Kristen dan kegiatan sosial jemaat.",
    icon: HandHeart,
    gradient: "from-[#8A5FA3] to-[#B587D0]",
    ring: "ring-[#8A5FA3]/30",
    accent: "#8A5FA3",
    filterAnggota: "PERNITA",
  },
  {
    code: "PEREDA",
    title: "PEREDA",
    subtitle: "Persekutuan Remaja & Pemuda",
    description: "Wadah pelayanan pemuda dan remaja: penginjilan, doa, KKR, mentoring, dan pembinaan iman.",
    icon: Flame,
    gradient: "from-[#3C8C4A] to-[#5FB86D]",
    ring: "ring-[#3C8C4A]/30",
    accent: "#3C8C4A",
    filterAnggota: "PEREDA",
  },
  {
    code: "PERSEMI",
    title: "PERSEMI",
    subtitle: "Persekutuan Sekolah Minggu",
    description: "Wadah pelayanan anak-anak Sekolah Minggu GPDP: pembelajaran Alkitab, kegiatan kreatif, dan pembinaan iman anak.",
    icon: Baby,
    gradient: "from-[#C8473D] to-[#E57B71]",
    ring: "ring-[#C8473D]/30",
    accent: "#C8473D",
    filterAnggota: "PERSEMI",
  },
  {
    code: "KOMISI",
    title: "Komisi Klasis",
    subtitle: "Bagian & Bidang Kerja",
    description: "Bagian Organisasi, Penginjilan, Doa, Bina Usaha, dan komisi khusus lainnya di Klasis.",
    icon: Layers,
    gradient: "from-[#B58B00] to-[#E5B52A]",
    ring: "ring-[#E5B52A]/30",
    accent: "#E5B52A",
    filterAnggota: null,
  },
];

export default function BidangKomisi() {
  const { user } = useAuth();
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // org code

  useEffect(() => {
    api.get("/bidang-komisi/overview")
      .then(r => setOverview(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat"))
      .finally(() => setLoading(false));
  }, []);

  const meta = (code) => ORGS.find(o => o.code === code);
  const stat = (code) => overview.find(o => o.code === code) || {};

  if (selected) {
    return <BidangDetail org={meta(selected)} stat={stat(selected)} onBack={() => setSelected(null)} user={user} />;
  }

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Organisasi</div>
        <h1 className="font-heading font-black text-3xl mt-1 flex items-center gap-3">
          <Grid2x2 className="h-7 w-7 text-secondary" /> Bidang & Komisi
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Wadah pelayanan GPDP Klasis Mimika: PERIA, PERNITA, PEREDA, PERSEMI, dan Komisi/Bagian Klasis. Klik salah satu kartu untuk melihat pengurus, anggota, dan program kerja.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(5)].map((_,i) => <div key={i} className="h-56 bg-muted rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bidang-grid">
          {ORGS.map(o => {
            const s = stat(o.code);
            const Icon = o.icon;
            return (
              <button
                key={o.code}
                onClick={() => setSelected(o.code)}
                className="text-left group focus:outline-none"
                data-testid={`card-bidang-${o.code}`}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 border-border/60 h-full group-hover:-translate-y-0.5">
                  <div className={`relative h-24 bg-gradient-to-br ${o.gradient} text-white p-5 flex items-center gap-4 overflow-hidden`}>
                    <div className="absolute inset-0 papua-motif opacity-[0.08]" />
                    <div className={`relative h-14 w-14 rounded-xl bg-white/95 ring-2 ${o.ring} flex items-center justify-center shrink-0 shadow-md`}>
                      <Icon className="h-7 w-7" style={{ color: o.accent }} />
                    </div>
                    <div className="relative min-w-0 flex-1">
                      <div className="font-heading font-black text-lg leading-tight truncate">{o.title}</div>
                      <div className="text-[0.7rem] tracking-widest uppercase text-white/80 mt-0.5">{o.subtitle}</div>
                    </div>
                    <ChevronRight className="relative h-5 w-5 text-white/70 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <CardContent className="p-5 space-y-3">
                    <p className="text-xs text-muted-foreground line-clamp-2">{o.description}</p>
                    {/* Ketua */}
                    <div className="border border-border rounded-lg p-2.5 bg-muted/30 flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Crown className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">Ketua</div>
                        <div className="text-xs font-semibold truncate">{s.ketua?.person_name || "Belum Ditetapkan"}</div>
                      </div>
                    </div>
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 pt-1">
                      <div className="text-center border-r border-border">
                        <div className="font-heading font-black text-lg text-primary">{s.pengurus_count || 0}</div>
                        <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">Pengurus</div>
                      </div>
                      <div className="text-center border-r border-border">
                        <div className="font-heading font-black text-lg text-secondary">{s.anggota_count || 0}</div>
                        <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">Anggota</div>
                      </div>
                      <div className="text-center">
                        <div className="font-heading font-black text-lg text-accent-foreground" style={{color: o.accent}}>{s.program_count || 0}</div>
                        <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">Program</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}

function BidangDetail({ org, stat, onBack, user }) {
  const [tab, setTab] = useState("pengurus");
  const [pengurus, setPengurus] = useState([]);
  const [anggota, setAnggota] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [agenda, setAgenda] = useState([]);
  const [kegiatan, setKegiatan] = useState([]);
  const [kas, setKas] = useState({ items: [], saldo: { masuk: 0, keluar: 0, saldo: 0, count: 0 } });
  const [undianEvents, setUndianEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [kegiatanOpen, setKegiatanOpen] = useState(false);
  const [kasOpen, setKasOpen] = useState(false);
  const [undianOpen, setUndianOpen] = useState(false);
  const [selectedUndianEvent, setSelectedUndianEvent] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, photos: [], index: 0, title: "" });
  const Icon = org.icon;
  const isKomisi = org.code === "KOMISI";
  const canManage = ["SUPER_ADMIN", "ADMIN_KLASIS", "SEKRETARIS_KLASIS", "GEMBALA_JEMAAT", "SEKRETARIS_JEMAAT"].includes(user?.role);

  const loadAll = async () => {
    setLoading(true);
    const calls = [api.get(`/positions?organization=${org.code}&active_only=true`)];
    if (!isKomisi) {
      calls.push(api.get(`/bidang-komisi/${org.code}/anggota`));
      calls.push(api.get(`/programs`));
      calls.push(api.get(`/wadah-agenda?wadah=${org.code}`));
      calls.push(api.get(`/kegiatan?wadah=${org.code}`));
      calls.push(api.get(`/wadah-kas?wadah=${org.code}`));
      calls.push(api.get(`/undian-events?wadah=${org.code}`));
    }
    try {
      const results = await Promise.all(calls);
      setPengurus(results[0].data);
      if (!isKomisi) {
        setAnggota(results[1].data);
        setPrograms((results[2].data || []).filter(p => p.bidang === org.code));
        setAgenda(results[3].data);
        setKegiatan(results[4].data);
        setKas(results[5].data);
        setUndianEvents(results[6].data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [org.code]);

  const openLightbox = (keg, i = 0) => setLightbox({ open: true, photos: keg.photos_base64 || [], index: i, title: keg.title });

  const filteredAnggota = useMemo(() => {
    const s = q.toLowerCase();
    if (!s) return anggota;
    return anggota.filter(a =>
      (a.name || "").toLowerCase().includes(s) ||
      (a.member_number || "").toLowerCase().includes(s) ||
      (a.church_short_name || "").toLowerCase().includes(s)
    );
  }, [anggota, q]);

  const filteredPengurus = useMemo(() => {
    const s = q.toLowerCase();
    if (!s) return pengurus;
    return pengurus.filter(p =>
      (p.person_name || "").toLowerCase().includes(s) ||
      (p.title || "").toLowerCase().includes(s)
    );
  }, [pengurus, q]);

  return (
    <AppShell>
      <button onClick={onBack} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mb-4" data-testid="btn-back-bidang">
        <ArrowLeft className="h-3 w-3" /> Kembali ke daftar Bidang & Komisi
      </button>

      {/* Hero */}
      <div className={`relative rounded-2xl bg-gradient-to-br ${org.gradient} text-white p-6 sm:p-8 mb-6 overflow-hidden shadow-lg`} data-testid="bidang-hero">
        <div className="absolute inset-0 papua-motif opacity-[0.08]" />
        <div className="relative flex items-center gap-4 flex-wrap">
          <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-white shadow-xl ring-2 ring-white/40 flex items-center justify-center">
            <Icon className="h-8 w-8 sm:h-10 sm:w-10" style={{ color: org.accent }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[0.7rem] tracking-[0.22em] uppercase text-white/80 font-bold">{org.subtitle}</div>
            <h1 className="font-heading font-black text-2xl sm:text-3xl leading-tight mt-1">{org.title}</h1>
            <p className="text-sm text-white/85 mt-2 max-w-xl">{org.description}</p>
          </div>
        </div>
        {/* KPI strip */}
        <div className="relative mt-6 grid grid-cols-3 gap-4 max-w-lg">
          <div className="border-l-2 border-white/30 pl-3">
            <div className="font-heading font-black text-2xl">{stat.pengurus_count || 0}</div>
            <div className="text-[0.6rem] uppercase tracking-widest text-white/70">Pengurus Aktif</div>
          </div>
          {!isKomisi && (
            <div className="border-l-2 border-white/30 pl-3">
              <div className="font-heading font-black text-2xl">{stat.anggota_count || 0}</div>
              <div className="text-[0.6rem] uppercase tracking-widest text-white/70">Anggota Klasis</div>
            </div>
          )}
          {!isKomisi && (
            <div className="border-l-2 border-white/30 pl-3">
              <div className="font-heading font-black text-2xl">{stat.program_count || 0}</div>
              <div className="text-[0.6rem] uppercase tracking-widest text-white/70">Program</div>
            </div>
          )}
        </div>
      </div>

      {/* Leadership Row (Ketua/Wakil/Sekretaris/Bendahara) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Ketua", data: stat.ketua, icon: Crown, tone: "primary" },
          { label: "Wakil Ketua", data: stat.wakil_ketua, icon: Users, tone: "secondary" },
          { label: "Sekretaris", data: stat.sekretaris, icon: Briefcase, tone: "accent" },
          { label: "Bendahara", data: stat.bendahara, icon: Layers, tone: "success" },
        ].map((role) => {
          const RoleIcon = role.icon;
          return (
            <Card key={role.label} className="hover:border-primary/30 transition-colors" data-testid={`leadership-${role.label.toLowerCase().replace(/\s/g,'-')}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <RoleIcon className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">{role.label}</div>
                  <div className="text-sm font-heading font-bold truncate">{role.data?.person_name || <em className="text-muted-foreground font-normal">Belum ditetapkan</em>}</div>
                  {role.data?.title && role.data?.title !== role.label && <div className="text-[0.65rem] text-muted-foreground truncate">{role.data.title}</div>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <TabsList data-testid="bidang-tabs" className="flex-wrap h-auto">
            <TabsTrigger value="pengurus" data-testid="tab-pengurus">Pengurus ({pengurus.length})</TabsTrigger>
            {!isKomisi && <TabsTrigger value="anggota" data-testid="tab-anggota">Anggota ({anggota.length})</TabsTrigger>}
            {!isKomisi && <TabsTrigger value="agenda" data-testid="tab-agenda"><Calendar className="h-3 w-3 mr-1" /> Kalender ({agenda.length})</TabsTrigger>}
            {!isKomisi && <TabsTrigger value="kegiatan" data-testid="tab-kegiatan"><Camera className="h-3 w-3 mr-1" /> Kegiatan ({kegiatan.length})</TabsTrigger>}
            {!isKomisi && <TabsTrigger value="kas" data-testid="tab-kas"><PiggyBank className="h-3 w-3 mr-1" /> Kas ({kas.saldo.count || 0})</TabsTrigger>}
            {!isKomisi && <TabsTrigger value="undian" data-testid="tab-undian"><Ticket className="h-3 w-3 mr-1" /> Undian ({undianEvents.length})</TabsTrigger>}
            {!isKomisi && <TabsTrigger value="program" data-testid="tab-program">Program Kerja ({programs.length})</TabsTrigger>}
          </TabsList>
          <div className="flex items-center gap-2 flex-wrap">
            {(tab === "anggota" || tab === "pengurus") && (
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder={`Cari ${tab}…`} value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 h-9" data-testid={`search-${tab}`} />
              </div>
            )}
            {!isKomisi && tab === "anggota" && canManage && (
              <Button size="sm" className="rounded-full" onClick={() => setAssignOpen(true)} data-testid="btn-assign-wadah" style={{ backgroundColor: org.accent, borderColor: org.accent }}>
                <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Tambah Anggota
              </Button>
            )}
            {!isKomisi && tab === "agenda" && canManage && (
              <Button size="sm" className="rounded-full" onClick={() => setAgendaOpen(true)} data-testid="btn-new-agenda" style={{ backgroundColor: org.accent, borderColor: org.accent }}>
                <CalendarPlus className="h-3.5 w-3.5 mr-1.5" /> Agenda Baru
              </Button>
            )}
            {!isKomisi && tab === "kegiatan" && canManage && (
              <Button size="sm" className="rounded-full" onClick={() => setKegiatanOpen(true)} data-testid="btn-new-kegiatan" style={{ backgroundColor: org.accent, borderColor: org.accent }}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Laporan Kegiatan
              </Button>
            )}
            {!isKomisi && tab === "kas" && canManage && (
              <Button size="sm" className="rounded-full" onClick={() => setKasOpen(true)} data-testid="btn-new-kas" style={{ backgroundColor: org.accent, borderColor: org.accent }}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Catat Kas
              </Button>
            )}
            {!isKomisi && tab === "undian" && canManage && (
              <Button size="sm" className="rounded-full" onClick={() => setUndianOpen(true)} data-testid="btn-new-undian" style={{ backgroundColor: org.accent, borderColor: org.accent }}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Undian Baru
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="pengurus">
          {loading ? (
            <div className="text-sm text-muted-foreground">Memuat pengurus…</div>
          ) : filteredPengurus.length === 0 ? (
            <EmptyState icon={Crown} text={`Belum ada pengurus ${org.title}.`} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pengurus-grid">
              {filteredPengurus.map(p => {
                const initials = (p.person_name||"?").split(" ").slice(0,2).map(s=>s[0]).join("").toUpperCase();
                const photoSrc = p.photo_url || p.photo_base64;
                return (
                  <Card key={p.id} className="hover:border-primary/40 transition-colors" data-testid={`pengurus-${p.id}`}>
                    <CardContent className="p-4 flex items-start gap-3">
                      {photoSrc ? (
                        <img src={photoSrc} alt={p.person_name} className="h-14 w-14 rounded-lg object-cover border border-border" />
                      ) : (
                        <div className="h-14 w-14 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-heading font-bold text-sm">{initials}</div>
                      )}
                      <div className="min-w-0 flex-1">
                        <Link to={`/kartu/${p.id}`} className="font-heading font-bold text-sm hover:text-primary transition-colors block truncate">{p.person_name}</Link>
                        <div className="text-xs text-primary font-medium truncate">{p.title}</div>
                        <div className="text-[0.65rem] text-muted-foreground mt-1">{p.period_start} → {p.period_end || "…"}</div>
                        <Link to={`/kartu/${p.id}`} className="text-[0.65rem] text-secondary hover:text-primary inline-flex items-center gap-0.5 mt-1"><IdCard className="h-3 w-3" /> Kartu</Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {!isKomisi && (
          <TabsContent value="anggota">
            {loading ? (
              <div className="text-sm text-muted-foreground">Memuat anggota…</div>
            ) : filteredAnggota.length === 0 ? (
              <EmptyState icon={User} text={`Belum ada anggota terdaftar di wadah ${org.title}. Anggota dapat diassign ke wadah di halaman Import Anggota.`} />
            ) : (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left p-3">Nama</th>
                        <th className="text-left p-3">No. Anggota</th>
                        <th className="text-left p-3">Jemaat</th>
                        <th className="text-left p-3">Komisi</th>
                        <th className="text-right p-3">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAnggota.map(a => {
                        const initials = (a.name||"?").split(" ").slice(0,2).map(s=>s[0]).join("").toUpperCase();
                        const photoSrc = a.photo_url || a.photo_base64;
                        return (
                          <tr key={a.id} className="border-t border-border hover:bg-muted/20" data-testid={`anggota-${a.id}`}>
                            <td className="p-3 flex items-center gap-3">
                              {photoSrc ? (
                                <img src={photoSrc} alt={a.name} className="h-8 w-8 rounded-full object-cover border border-border" />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[0.65rem] font-bold">{initials}</div>
                              )}
                              <span className="font-medium">{a.name}</span>
                            </td>
                            <td className="p-3 font-mono text-[0.7rem] text-muted-foreground">{a.member_number}</td>
                            <td className="p-3 text-xs">{a.church_short_name || "—"}</td>
                            <td className="p-3 text-xs">{a.komisi || <em className="text-muted-foreground/70">—</em>}</td>
                            <td className="p-3 text-right">
                              <Link to={`/kartu-anggota/${a.id}`} className="text-[0.7rem] text-primary hover:text-secondary inline-flex items-center gap-1" data-testid={`link-kartu-${a.id}`}>
                                <IdCard className="h-3 w-3" /> Kartu
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}

        {/* KAS Wadah */}
        {!isKomisi && (
          <TabsContent value="kas">
            {loading ? (
              <div className="text-sm text-muted-foreground">Memuat kas…</div>
            ) : (
              <div className="space-y-4" data-testid="kas-tab">
                {/* Saldo summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                      <ArrowDownCircle className="h-9 w-9 text-emerald-600 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">Total Kas Masuk</div>
                        <div className="font-heading font-black text-lg font-mono text-emerald-700">Rp {kas.saldo.masuk.toLocaleString("id-ID")}</div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                      <ArrowUpCircle className="h-9 w-9 text-rose-600 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">Total Kas Keluar</div>
                        <div className="font-heading font-black text-lg font-mono text-rose-700">Rp {kas.saldo.keluar.toLocaleString("id-ID")}</div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-2" style={{borderColor: org.accent}}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <PiggyBank className="h-9 w-9 shrink-0" style={{color: org.accent}} />
                      <div className="min-w-0">
                        <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">Saldo {org.title}</div>
                        <div className="font-heading font-black text-lg font-mono" style={{color: org.accent}}>Rp {kas.saldo.saldo.toLocaleString("id-ID")}</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {kas.items.length === 0 ? (
                  <EmptyState icon={PiggyBank} text={`Belum ada transaksi kas untuk ${org.title}. Bendahara wadah dapat mencatat penerimaan (persembahan, undian, sumbangan) dan pengeluaran (hadiah, konsumsi, transportasi).`} action={canManage && <Button onClick={() => setKasOpen(true)} className="rounded-full" style={{backgroundColor:org.accent}}><Plus className="h-3.5 w-3.5 mr-1.5" /> Catat Transaksi</Button>} />
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                          <tr>
                            <th className="text-left p-3">Tanggal</th>
                            <th className="text-left p-3">Uraian</th>
                            <th className="text-left p-3">Kategori</th>
                            <th className="text-right p-3">Masuk</th>
                            <th className="text-right p-3">Keluar</th>
                            <th className="text-right p-3">Aksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kas.items.map((t) => (
                            <tr key={t.id} className="border-t border-border hover:bg-muted/20" data-testid={`kas-${t.id}`}>
                              <td className="p-3 text-xs text-muted-foreground">{new Date(t.tanggal).toLocaleDateString("id-ID", {day:"2-digit", month:"short", year:"numeric"})}</td>
                              <td className="p-3">
                                <div className="font-medium">{t.uraian}</div>
                                {t.keterangan && <div className="text-[0.65rem] text-muted-foreground">{t.keterangan}</div>}
                              </td>
                              <td className="p-3 text-xs"><span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[0.6rem] uppercase tracking-widest font-bold">{t.kategori}</span></td>
                              <td className="p-3 text-right font-mono text-xs text-emerald-700 font-semibold">
                                {t.jenis === "MASUK" ? `Rp ${t.jumlah.toLocaleString("id-ID")}` : "—"}
                              </td>
                              <td className="p-3 text-right font-mono text-xs text-rose-700 font-semibold">
                                {t.jenis === "KELUAR" ? `Rp ${t.jumlah.toLocaleString("id-ID")}` : "—"}
                              </td>
                              <td className="p-3 text-right">
                                {canManage && (
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm("Hapus transaksi ini?")) return;
                                      try { await api.delete(`/wadah-kas/${t.id}`); toast.success("Dihapus"); loadAll(); }
                                      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                                    }}
                                    className="p-1 text-muted-foreground hover:text-destructive"
                                    data-testid={`btn-del-kas-${t.id}`}
                                  ><Trash2 className="h-3 w-3" /></button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        )}

        {/* UNDIAN Digital */}
        {!isKomisi && (
          <TabsContent value="undian">
            {loading ? (
              <div className="text-sm text-muted-foreground">Memuat undian…</div>
            ) : undianEvents.length === 0 ? (
              <EmptyState icon={Ticket} text={`Belum ada event undian untuk ${org.title}. Buat event undian baru untuk generate kupon berkode QR unik.`} action={canManage && <Button onClick={() => setUndianOpen(true)} className="rounded-full" style={{backgroundColor:org.accent}}><Plus className="h-3.5 w-3.5 mr-1.5" /> Undian Baru</Button>} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="undian-list">
                {undianEvents.map(ev => (
                  <Card key={ev.id} className={`overflow-hidden hover:shadow-md transition-shadow ${ev.status === 'CLOSED' ? 'opacity-90' : ''}`} data-testid={`undian-ev-${ev.id}`}>
                    <div className="relative p-4 text-white" style={{background: `linear-gradient(135deg, ${org.accent} 0%, ${org.accent}CC 100%)`}}>
                      <div className="absolute inset-0 papua-motif opacity-[0.08]" />
                      <div className="relative flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[0.65rem] tracking-widest uppercase opacity-90 font-bold">{ev.status === 'CLOSED' ? "Selesai" : "Undian Aktif"}</div>
                          <div className="font-heading font-black text-base mt-0.5 leading-tight">{ev.title}</div>
                          <div className="text-[0.7rem] opacity-90 mt-1">{new Date(ev.event_date).toLocaleDateString("id-ID", {day:"numeric",month:"long",year:"numeric"})}{ev.location ? ` · ${ev.location}` : ''}</div>
                        </div>
                        <Ticket className="h-6 w-6 opacity-80 shrink-0" />
                      </div>
                    </div>
                    <CardContent className="p-4">
                      {ev.hadiah && <div className="text-xs mb-2"><strong>Hadiah:</strong> {ev.hadiah}</div>}
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center border-r border-border">
                          <div className="font-heading font-black text-lg">{ev.coupon_count}</div>
                          <div className="text-[0.6rem] uppercase text-muted-foreground tracking-widest">Kupon</div>
                        </div>
                        <div className="text-center border-r border-border">
                          <div className="font-heading font-black text-lg text-emerald-700">{ev.redeemed_count}</div>
                          <div className="text-[0.6rem] uppercase text-muted-foreground tracking-widest">Terpakai</div>
                        </div>
                        <div className="text-center">
                          <div className="font-heading font-black text-lg" style={{color:org.accent}}>{ev.winner_coupon_id ? "✓" : "—"}</div>
                          <div className="text-[0.6rem] uppercase text-muted-foreground tracking-widest">Pemenang</div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => setSelectedUndianEvent(ev)} data-testid={`btn-view-undian-${ev.id}`}>
                          <QrCode className="h-3 w-3 mr-1" /> Lihat Kupon
                        </Button>
                        {canManage && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Hapus event undian "${ev.title}"? Semua kupon juga terhapus.`)) return;
                              try { await api.delete(`/undian-events/${ev.id}`); toast.success("Dihapus"); loadAll(); }
                              catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                            }}
                            className="ml-auto text-[0.65rem] text-muted-foreground hover:text-destructive"
                            data-testid={`btn-del-undian-${ev.id}`}
                          ><Trash2 className="h-3 w-3" /></button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {!isKomisi && (
          <TabsContent value="program">
            {loading ? (
              <div className="text-sm text-muted-foreground">Memuat program…</div>
            ) : programs.length === 0 ? (
              <EmptyState icon={Target} text={`Belum ada program kerja untuk bidang ${org.title}.`} action={<Link to="/program"><Button variant="outline" size="sm" className="rounded-full">Buka Program Kerja</Button></Link>} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="program-grid">
                {programs.map(p => (
                  <ProgramCard key={p.id} program={p} org={org} canManage={canManage} onDone={loadAll} />
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* AGENDA (Kalender Wadah) */}
        {!isKomisi && (
          <TabsContent value="agenda">
            {loading ? (
              <div className="text-sm text-muted-foreground">Memuat agenda…</div>
            ) : agenda.length === 0 ? (
              <EmptyState
                icon={Calendar}
                text={`Belum ada agenda untuk ${org.title}. Contoh: Retret PERIA, KKR Pemuda PERSEMI, kunjungan PEREDA, Doa Puasa PERNITA.`}
                action={canManage && <Button onClick={() => setAgendaOpen(true)} className="rounded-full" style={{backgroundColor:org.accent}}><CalendarPlus className="h-3.5 w-3.5 mr-1.5" /> Buat Agenda</Button>}
              />
            ) : (
              <div className="space-y-3" data-testid="agenda-list">
                {agenda.map(a => {
                  const past = new Date(a.date) < new Date(new Date().toISOString().slice(0,10));
                  return (
                    <Card key={a.id} className="hover:shadow-md transition-shadow" data-testid={`agenda-${a.id}`}>
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="text-center shrink-0 border-r border-border pr-4">
                          <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground">{new Date(a.date).toLocaleDateString('id-ID', { month:'short' })}</div>
                          <div className="font-heading font-black text-2xl leading-none mt-1" style={{color: past ? '#94a3b8' : org.accent}}>{new Date(a.date).getDate()}</div>
                          <div className="text-[0.6rem] uppercase text-muted-foreground mt-0.5">{new Date(a.date).getFullYear()}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div>
                              <div className="font-heading font-bold text-sm">{a.title}</div>
                              <div className="flex items-center gap-3 flex-wrap mt-1 text-[0.7rem] text-muted-foreground">
                                {a.time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {a.time}</span>}
                                {a.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location}</span>}
                                {a.pic && <span>PIC: {a.pic}</span>}
                              </div>
                              {a.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{a.description}</p>}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[0.6rem] px-2 py-0.5 rounded-full uppercase tracking-widest font-bold" style={{backgroundColor:`${org.accent}22`, color:org.accent}}>{a.category}</span>
                              {past && <StatusBadge status="muted" label="Lewat" />}
                              {canManage && (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Hapus agenda "${a.title}"?`)) return;
                                    try { await api.delete(`/wadah-agenda/${a.id}`); toast.success("Dihapus"); loadAll(); }
                                    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                                  }}
                                  className="p-1 text-muted-foreground hover:text-destructive"
                                  data-testid={`btn-del-agenda-${a.id}`}
                                ><Trash2 className="h-3 w-3" /></button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        )}

        {/* KEGIATAN (Laporan + Foto) */}
        {!isKomisi && (
          <TabsContent value="kegiatan">
            {loading ? (
              <div className="text-sm text-muted-foreground">Memuat kegiatan…</div>
            ) : kegiatan.length === 0 ? (
              <EmptyState
                icon={Camera}
                text={`Belum ada laporan kegiatan ${org.title}. Upload foto dan laporan setelah kegiatan selesai.`}
                action={canManage && <Button onClick={() => setKegiatanOpen(true)} className="rounded-full" style={{backgroundColor:org.accent}}><Plus className="h-3.5 w-3.5 mr-1.5" /> Buat Laporan</Button>}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="kegiatan-grid">
                {kegiatan.map(k => (
                  <Card key={k.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`keg-${k.id}`}>
                    {k.photo_count > 0 && k.photos_base64 && k.photos_base64[0] && (
                      <button onClick={() => openLightbox(k, 0)} className="w-full aspect-video bg-muted overflow-hidden group">
                        <img src={k.photos_base64[0]} alt={k.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </button>
                    )}
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="text-[0.65rem] uppercase tracking-widest font-bold" style={{color: org.accent}}>{k.category} • {new Date(k.date).toLocaleDateString('id-ID', {day:'numeric',month:'short',year:'numeric'})}</div>
                        {k.photo_count > 0 && <span className="text-[0.6rem] text-muted-foreground inline-flex items-center gap-1"><Camera className="h-3 w-3" />{k.photo_count}</span>}
                      </div>
                      <div className="font-heading font-bold text-sm">{k.title}</div>
                      {k.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{k.description}</p>}
                      <div className="mt-2 text-[0.65rem] text-muted-foreground">{k.location} · oleh {k.created_by}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Bulk Assign Anggota Dialog */}
      {!isKomisi && (
        <BulkAssignDialog
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          wadah={org}
          onDone={loadAll}
          currentAnggotaIds={new Set(anggota.map(a => a.id))}
        />
      )}

      {/* Agenda Dialog */}
      {!isKomisi && (
        <AgendaDialog
          open={agendaOpen}
          onClose={() => setAgendaOpen(false)}
          wadah={org}
          onDone={loadAll}
        />
      )}

      {/* Kegiatan (Laporan Foto) Dialog */}
      {!isKomisi && (
        <KegiatanDialog
          open={kegiatanOpen}
          onClose={() => setKegiatanOpen(false)}
          wadah={org}
          onDone={loadAll}
        />
      )}

      {/* Kas Dialog */}
      {!isKomisi && (
        <KasDialog
          open={kasOpen}
          onClose={() => setKasOpen(false)}
          wadah={org}
          onDone={loadAll}
        />
      )}

      {/* Undian Baru Dialog */}
      {!isKomisi && (
        <UndianDialog
          open={undianOpen}
          onClose={() => setUndianOpen(false)}
          wadah={org}
          onDone={loadAll}
        />
      )}

      {/* Undian Event Detail (Kupon Sheet) */}
      {selectedUndianEvent && (
        <UndianEventDetail
          eventId={selectedUndianEvent.id}
          wadah={org}
          onClose={() => setSelectedUndianEvent(null)}
          onDone={loadAll}
        />
      )}

      <PhotoLightbox
        open={lightbox.open}
        photos={lightbox.photos}
        initialIndex={lightbox.index}
        onClose={() => setLightbox({ ...lightbox, open: false })}
        title={lightbox.title}
      />
    </AppShell>
  );
}



// ------------------ Bulk Assign Dialog ------------------
function BulkAssignDialog({ open, onClose, wadah, onDone, currentAnggotaIds }) {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // Fetch all anggota; exclude those already in this wadah
    const url = showAll ? "/anggota?limit=2000" : "/anggota?limit=2000";
    api.get(url)
      .then(r => {
        const rows = (r.data || []).filter(a => !currentAnggotaIds.has(a.id));
        setCandidates(rows);
      })
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, showAll]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return candidates.filter(a =>
      (!s || (a.name||"").toLowerCase().includes(s) || (a.member_number||"").toLowerCase().includes(s)) &&
      (showAll || !a.wadah) // by default show only those without wadah
    );
  }, [candidates, q, showAll]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(a => a.id)));
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error("Pilih minimal 1 anggota"); return; }
    setSaving(true);
    try {
      const r = await api.post("/anggota/bulk-assign-wadah", { anggota_ids: [...selected], wadah: wadah.code });
      toast.success(`${r.data.modified} anggota di-assign ke ${wadah.title}`);
      setSelected(new Set()); setQ("");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Tambah Anggota ke {wadah.title}</DialogTitle>
          <DialogDescription>Pilih anggota dari 8 jemaat untuk masuk ke wadah <strong>{wadah.title}</strong>. Anggota yang sudah punya wadah tersembunyi (centang "Tampilkan semua" untuk memindahkan dari wadah lain).</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Cari nama atau no. anggota…" value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 h-9" data-testid="input-search-candidates" />
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={showAll} onChange={(e)=>setShowAll(e.target.checked)} data-testid="checkbox-show-all" />
            Tampilkan yang sudah punya wadah
          </label>
          <button onClick={toggleAll} className="text-xs text-primary hover:underline" data-testid="btn-toggle-all">
            {selected.size === filtered.length && filtered.length > 0 ? "Batal semua" : `Pilih ${filtered.length}`}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto border border-border rounded-md">
          {loading ? (
            <div className="text-sm text-muted-foreground p-6 text-center">Memuat kandidat…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground p-6 text-center">
              {candidates.length === 0 ? "Semua anggota sudah masuk wadah — centang 'Tampilkan semua'." : "Tidak ada hasil untuk pencarian."}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map(a => {
                const on = selected.has(a.id);
                return (
                  <li key={a.id}>
                    <button
                      type="button" onClick={() => toggle(a.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/30 text-left transition-colors ${on ? 'bg-primary/5' : ''}`}
                      data-testid={`cand-${a.id}`}
                    >
                      <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                        {on && <span className="text-primary-foreground text-[0.6rem]">✓</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{a.name}</div>
                        <div className="text-[0.65rem] text-muted-foreground truncate">
                          {a.member_number}
                          {a.wadah && <span className="ml-2 inline-block px-1.5 rounded bg-muted text-muted-foreground text-[0.55rem]">Sekarang: {a.wadah}</span>}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <DialogFooter>
          <div className="text-xs text-muted-foreground mr-auto">{selected.size} anggota dipilih</div>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={saving || selected.size === 0} data-testid="btn-confirm-assign" style={{ backgroundColor: wadah.accent }}>
            {saving ? "Menyimpan…" : `Assign ${selected.size} ke ${wadah.code}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Agenda Dialog ------------------
const AGENDA_CATEGORIES = ["KEGIATAN", "RETRET", "KUNJUNGAN", "RAPAT", "IBADAH", "LAINNYA"];
function AgendaDialog({ open, onClose, wadah, onDone }) {
  const [form, setForm] = useState({ title: "", description: "", date: new Date().toISOString().slice(0,10), time: "", location: "", category: "KEGIATAN", pic: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ title: "", description: "", date: new Date().toISOString().slice(0,10), time: "", location: "", category: "KEGIATAN", pic: "" });
  }, [open]);
  const submit = async () => {
    if (!form.title || !form.date) { toast.error("Judul & tanggal wajib"); return; }
    setSaving(true);
    try {
      await api.post("/wadah-agenda", { ...form, wadah: wadah.code });
      toast.success("Agenda tercatat");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agenda Baru {wadah.title}</DialogTitle>
          <DialogDescription>Retret, kunjungan, rapat, KKR, atau kegiatan khusus wadah.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Judul Agenda</Label>
            <Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} placeholder="Contoh: Retret PERIA 2027" data-testid="input-agenda-title" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                <SelectTrigger data-testid="select-agenda-category"><SelectValue /></SelectTrigger>
                <SelectContent>{AGENDA_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tanggal</Label>
              <Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} data-testid="input-agenda-date" />
            </div>
            <div>
              <Label>Waktu</Label>
              <Input type="time" value={form.time} onChange={(e)=>setForm({...form, time:e.target.value})} data-testid="input-agenda-time" />
            </div>
          </div>
          <div>
            <Label>Lokasi</Label>
            <Input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} placeholder="Kantor Klasis / Aula / Wisma…" data-testid="input-agenda-location" />
          </div>
          <div>
            <Label>PIC / Koordinator</Label>
            <Input value={form.pic} onChange={(e)=>setForm({...form, pic:e.target.value})} placeholder="Nama koordinator" data-testid="input-agenda-pic" />
          </div>
          <div>
            <Label>Keterangan</Label>
            <Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} placeholder="Rincian, tema, sasaran peserta…" data-testid="input-agenda-desc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-save-agenda" style={{ backgroundColor: wadah.accent }}>{saving ? "Menyimpan…" : "Simpan Agenda"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Kegiatan (Laporan) Dialog ------------------
const KEG_CATEGORIES = [
  { key: "PELAYANAN", label: "Pelayanan" },
  { key: "IBADAH", label: "Ibadah" },
  { key: "SOSIAL", label: "Sosial" },
  { key: "BINA_USAHA", label: "Bina Usaha" },
  { key: "RAPAT", label: "Rapat" },
  { key: "LAINNYA", label: "Lainnya" },
];
function KegiatanDialog({ open, onClose, wadah, onDone }) {
  const [form, setForm] = useState({ title: "", description: "", date: new Date().toISOString().slice(0,10), location: "", category: "PELAYANAN", visibility: "INTERNAL" });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setForm({ title: "", description: "", date: new Date().toISOString().slice(0,10), location: "", category: "PELAYANAN", visibility: "INTERNAL" }); setPhotos([]); }
  }, [open]);

  const addPhotos = async (files) => {
    for (const f of Array.from(files)) {
      if (f.size > 1_800_000) { toast.error(`${f.name}: >1.5MB`); continue; }
      const b64 = await fileToBase64(f);
      setPhotos((p) => [...p, b64]);
    }
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.title || !form.date) { toast.error("Judul & tanggal wajib"); return; }
    setSaving(true);
    try {
      await api.post("/kegiatan", {
        ...form,
        wadah: wadah.code,
        scope: "KLASIS",
        photos_base64: photos,
      });
      toast.success("Laporan kegiatan tersimpan");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Laporan Kegiatan {wadah.title}</DialogTitle>
          <DialogDescription>Catat kegiatan yang telah/sedang berlangsung dengan foto dan ringkasan.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Judul Kegiatan</Label>
            <Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} placeholder="Contoh: KKR Pemuda PERSEMI 2026" data-testid="input-keg-title" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                <SelectTrigger data-testid="select-keg-category"><SelectValue /></SelectTrigger>
                <SelectContent>{KEG_CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tanggal</Label>
              <Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} data-testid="input-keg-date" />
            </div>
            <div>
              <Label>Visibilitas</Label>
              <Select value={form.visibility} onValueChange={(v)=>setForm({...form, visibility:v})}>
                <SelectTrigger data-testid="select-keg-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INTERNAL">Internal</SelectItem>
                  <SelectItem value="PUBLIC">Publik</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Lokasi</Label>
            <Input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} data-testid="input-keg-location" />
          </div>
          <div>
            <Label>Deskripsi / Laporan</Label>
            <Textarea rows={4} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} placeholder="Ringkasan pelaksanaan, jumlah peserta, hasil kesepakatan…" data-testid="input-keg-desc" />
          </div>
          <div>
            <Label>Foto Kegiatan ({photos.length})</Label>
            <div className="mt-1 grid grid-cols-4 sm:grid-cols-6 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded overflow-hidden bg-muted">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-rose-600 text-white rounded-full p-0.5" data-testid={`btn-remove-photo-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="aspect-square rounded border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary cursor-pointer transition-colors" data-testid="label-upload-photos">
                <Upload className="h-4 w-4 mb-1" />
                <span className="text-[0.6rem]">Foto</span>
                <input type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files && addPhotos(e.target.files)} data-testid="input-keg-photos" />
              </label>
            </div>
            <div className="text-[0.65rem] text-muted-foreground mt-1">Format JPG/PNG, maks 1.5MB per foto</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-save-keg" style={{ backgroundColor: wadah.accent }}>{saving ? "Menyimpan…" : "Simpan Laporan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ icon: Icon, text, action }) {
  return (
    <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="empty-state">
      <Icon className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
      <div className="text-sm text-muted-foreground max-w-md mx-auto">{text}</div>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ------------------ Program Card + Realisasi ------------------
function ProgramCard({ program, org, canManage, onDone }) {
  const [realOpen, setRealOpen] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const isUndian = /undian|lotre/i.test(program.name);
  const budget = program.budget || 0;
  const realisasi = program.realisasi || 0;
  const pct = budget > 0 ? Math.min(100, Math.round((realisasi / budget) * 100)) : 0;

  return (
    <Card data-testid={`program-${program.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="text-[0.65rem] uppercase tracking-widest text-secondary font-bold">{program.year}</div>
          {program.status && <StatusBadge status={program.status} />}
        </div>
        <div className="font-heading font-bold text-sm">{program.name}</div>
        {program.tujuan && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{program.tujuan}</p>}

        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex justify-between text-[0.65rem] text-muted-foreground mb-1">
            <span>Progress</span>
            <span className="font-mono font-bold text-foreground">{program.progress || 0}% · {pct}% terealisasi</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden relative">
            <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{width: `${program.progress || 0}%`, backgroundColor: org.accent}} />
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-2 text-[0.7rem] text-muted-foreground">
          <div>Anggaran<div className="font-mono text-foreground font-semibold">Rp {budget.toLocaleString("id-ID")}</div></div>
          <div>Realisasi<div className="font-mono text-foreground font-semibold">Rp {realisasi.toLocaleString("id-ID")}</div></div>
        </div>
        {program.pic && <div className="text-[0.65rem] text-muted-foreground mt-2">PIC: {program.pic}</div>}

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {canManage && (
            <button onClick={() => setRealOpen(true)} className="text-[0.7rem] font-medium text-primary hover:text-secondary inline-flex items-center gap-1" data-testid={`btn-realisasi-${program.id}`}>
              <Wallet className="h-3 w-3" /> Catat Realisasi
            </button>
          )}
          {isUndian && (
            <button onClick={() => setPosterOpen(true)} className="text-[0.7rem] font-medium hover:opacity-80 inline-flex items-center gap-1 ml-auto" style={{color: org.accent}} data-testid={`btn-poster-${program.id}`}>
              <FileText className="h-3 w-3" /> Buat Poster Undian
            </button>
          )}
        </div>
      </CardContent>

      {realOpen && (
        <RealisasiDialog
          program={program}
          org={org}
          onClose={() => setRealOpen(false)}
          onDone={onDone}
        />
      )}
      {posterOpen && (
        <PosterUndianDialog
          program={program}
          org={org}
          onClose={() => setPosterOpen(false)}
        />
      )}
    </Card>
  );
}

function RealisasiDialog({ program, org, onClose, onDone }) {
  const [realisasi, setRealisasi] = useState(program.realisasi || 0);
  const [progress, setProgress] = useState(program.progress || 0);
  const [status, setStatus] = useState(program.status || "PLANNED");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/programs/${program.id}/realisasi`, {
        realisasi: Number(realisasi) || 0,
        progress: Number(progress) || 0,
        status,
        notes: notes || null,
      });
      toast.success("Realisasi tercatat");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal"); }
    finally { setSaving(false); }
  };

  const budget = program.budget || 0;
  const pct = budget > 0 ? Math.min(200, Math.round((Number(realisasi) / budget) * 100)) : 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Catat Realisasi Program</DialogTitle>
          <DialogDescription>{program.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border rounded-md p-3 bg-muted/20 flex items-center gap-3">
            <TrendingUp className="h-6 w-6 text-primary shrink-0" />
            <div className="flex-1">
              <div className="text-xs text-muted-foreground">Anggaran</div>
              <div className="font-mono font-bold text-lg">Rp {budget.toLocaleString("id-ID")}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Persentase</div>
              <div className="font-bold text-lg" style={{color: org.accent}}>{pct}%</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Realisasi (Rp)</Label>
              <Input type="number" value={realisasi} onChange={(e)=>setRealisasi(e.target.value)} placeholder="0" data-testid="input-realisasi-value" />
            </div>
            <div>
              <Label>Progress (%)</Label>
              <Input type="number" min="0" max="100" value={progress} onChange={(e)=>setProgress(e.target.value)} data-testid="input-realisasi-progress" />
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="select-realisasi-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PLANNED">Planned (Belum Dimulai)</SelectItem>
                <SelectItem value="IN_PROGRESS">Berjalan</SelectItem>
                <SelectItem value="DONE">Selesai</SelectItem>
                <SelectItem value="CANCELLED">Dibatalkan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Catatan (opsional)</Label>
            <Textarea rows={2} value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Contoh: Ibadah PERIA 15 Nov 2026, penerimaan undian Rp 2.500.000…" data-testid="input-realisasi-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-save-realisasi" style={{backgroundColor: org.accent}}>{saving ? "Menyimpan…" : "Simpan Realisasi"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Poster Undian ------------------
function PosterUndianDialog({ program, org, onClose }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0,10),
    time: "18:00",
    location: "GPDP Jemaat El Olam Timika",
    tema: "Ibadah Bergilir PERIA Klasis",
    firman: "Amsal 3:5-6",
    pembicara: "Pdm. Eddy Lesnussa, Amd.T",
    hadiah: "Bingkisan pemenang minggu lalu",
    tuan_rumah: "Bpk. Kristian Manggaprouw",
    kontak: "0812-xxxx-xxxx",
  });

  const fmtDate = (d) => new Date(d).toLocaleDateString("id-ID", { weekday:"long", day:"numeric", month:"long", year:"numeric" });
  const print = () => window.print();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        <div className="flex items-center justify-between p-3 border-b border-border print:hidden bg-muted/20">
          <div className="text-xs text-muted-foreground">Poster Ibadah Undian PERIA — {program.name}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={print} data-testid="btn-print-poster">
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Cetak / PDF / Bagikan
            </Button>
          </div>
        </div>

        {/* Editable form (hidden in print) */}
        <div className="grid md:grid-cols-2 gap-4 p-4 print:hidden border-b border-border">
          <div>
            <Label>Tanggal</Label>
            <Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} data-testid="input-poster-date" />
          </div>
          <div>
            <Label>Waktu</Label>
            <Input value={form.time} onChange={(e)=>setForm({...form, time:e.target.value})} data-testid="input-poster-time" />
          </div>
          <div>
            <Label>Tuan Rumah (Jemaat)</Label>
            <Input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} data-testid="input-poster-location" />
          </div>
          <div>
            <Label>Tema Ibadah</Label>
            <Input value={form.tema} onChange={(e)=>setForm({...form, tema:e.target.value})} data-testid="input-poster-tema" />
          </div>
          <div>
            <Label>Nats Firman</Label>
            <Input value={form.firman} onChange={(e)=>setForm({...form, firman:e.target.value})} data-testid="input-poster-firman" />
          </div>
          <div>
            <Label>Pembicara</Label>
            <Input value={form.pembicara} onChange={(e)=>setForm({...form, pembicara:e.target.value})} data-testid="input-poster-pembicara" />
          </div>
          <div>
            <Label>Hadiah Undian Kali Ini</Label>
            <Input value={form.hadiah} onChange={(e)=>setForm({...form, hadiah:e.target.value})} data-testid="input-poster-hadiah" />
          </div>
          <div>
            <Label>Penyedia Hadiah (Pemenang Sebelumnya)</Label>
            <Input value={form.tuan_rumah} onChange={(e)=>setForm({...form, tuan_rumah:e.target.value})} data-testid="input-poster-tuan-rumah" />
          </div>
        </div>

        {/* Poster canvas — printable */}
        <div className="surat-print-root px-4 py-4 bg-white" data-testid="poster-canvas">
          <div className="relative overflow-hidden rounded-xl border-4" style={{ borderColor: org.accent, aspectRatio: "3/4", background: "linear-gradient(180deg, #17365D 0%, #276AA3 100%)" }}>
            <div className="absolute inset-0 papua-motif opacity-[0.08]" />
            {/* Gold decorative rings */}
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full border-2 opacity-30" style={{ borderColor: org.accent, borderStyle: "dashed" }} />
            <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full border-2 opacity-20" style={{ borderColor: org.accent }} />

            <div className="relative flex flex-col h-full text-white p-8">
              {/* Top strip */}
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo-gpdp.png" alt="Logo GPDP" className="h-16 w-16 object-contain bg-white rounded-full p-1 shadow-lg" />
                <div>
                  <div className="text-[0.6rem] tracking-[0.28em] uppercase font-bold" style={{color: org.accent}}>PERSEKUTUAN PRIA — PERIA</div>
                  <div className="font-heading font-black text-lg leading-tight">GPDP KLASIS MIMIKA</div>
                  <div className="text-[0.6rem] text-white/70 tracking-wider uppercase">Periode 2026–2031</div>
                </div>
              </div>

              {/* Hero title */}
              <div className="mt-2 mb-4 flex-1 flex flex-col justify-center text-center">
                <div className="text-[0.7rem] tracking-[0.22em] font-bold uppercase" style={{color: org.accent}}>Ibadah Bergilir + Undian Berkat</div>
                <div className="font-heading font-black text-3xl sm:text-5xl leading-[0.95] mt-3">{form.tema}</div>
                <div className="mt-3 text-sm sm:text-base italic opacity-90">"{form.firman}"</div>

                {/* Divider */}
                <div className="mx-auto mt-6 mb-4 flex items-center gap-2">
                  <div className="h-px w-12" style={{ backgroundColor: org.accent }} />
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: org.accent }} />
                  <div className="h-px w-12" style={{ backgroundColor: org.accent }} />
                </div>

                <div className="text-2xl sm:text-3xl font-heading font-black mt-2" style={{color: org.accent}}>
                  {fmtDate(form.date)}
                </div>
                <div className="text-lg font-heading font-bold mt-1">Pukul {form.time} WIT</div>
                <div className="mt-3 text-sm">
                  <div className="text-[0.65rem] tracking-widest uppercase text-white/70">Tuan Rumah</div>
                  <div className="font-bold">{form.location}</div>
                </div>
              </div>

              {/* Info undian bar */}
              <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                <div className="border-2 rounded-lg p-3 backdrop-blur-sm" style={{ borderColor: org.accent, backgroundColor: `${org.accent}12` }}>
                  <div className="text-[0.6rem] tracking-widest uppercase font-bold" style={{color: org.accent}}>Hadiah Undian</div>
                  <div className="font-heading font-bold text-sm mt-0.5">{form.hadiah}</div>
                  <div className="text-[0.65rem] opacity-80 mt-1">disediakan oleh: {form.tuan_rumah}</div>
                </div>
                <div className="border-2 rounded-lg p-3 backdrop-blur-sm" style={{ borderColor: org.accent, backgroundColor: `${org.accent}12` }}>
                  <div className="text-[0.6rem] tracking-widest uppercase font-bold" style={{color: org.accent}}>Pembicara</div>
                  <div className="font-heading font-bold text-sm mt-0.5">{form.pembicara}</div>
                  <div className="text-[0.65rem] opacity-80 mt-1">Firman: {form.firman}</div>
                </div>
              </div>

              {/* Aturan main singkat */}
              <div className="mt-4 border border-white/30 rounded-lg p-3 text-xs bg-white/5">
                <div className="text-[0.6rem] tracking-widest uppercase font-bold mb-2" style={{color: org.accent}}>Aturan Undian Berkat</div>
                <ol className="space-y-1 text-white/90 list-decimal pl-5">
                  <li>Setiap anggota PERIA berhak ikut undian pada ibadah PERIA Klasis.</li>
                  <li>Anggota yang memenangkan undian menerima hadiah pada ibadah tersebut.</li>
                  <li>Pemenang berkewajiban menyediakan hadiah untuk undian ibadah berikutnya.</li>
                </ol>
              </div>

              {/* Footer */}
              <div className="mt-4 flex items-center justify-between pt-3 border-t border-white/20 text-[0.65rem] text-white/70">
                <div>Info: {form.kontak}</div>
                <div className="text-right">
                  <div>SIMPELKAMI GPDP</div>
                  <div>"Terintegrasi dalam Data, Tertib dalam Pelayanan"</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
